import React, { useState, useRef, useEffect, useCallback } from 'react';
import axios from 'axios';
import { useAuth } from '../contexts/AuthContext';
import '../styles/reachCurveTool.css';

// Components
import ScenarioPanel from '../components/reach/ScenarioPanel';
import ReachVisualizer from '../components/reach/ReachVisualizer';
import ComparisonView from '../components/reach/ComparisonView';
import ChatInterface from '../components/reach/ChatInterface';

function ReachCurveTool() {
  const { user, isAuthenticated, refreshToken } = useAuth();
  const [scenarios, setScenarios] = useState([]);
  const [activeScenario, setActiveScenario] = useState(null);
  const [comparison, setComparison] = useState(null);
  const [loading, setLoading] = useState(false);
  const [chatHistory, setChatHistory] = useState([]);

  // Initialize with a default scenario
  useEffect(() => {
    if (isAuthenticated && scenarios.length === 0) {
      createNewScenario();
    }
  }, [isAuthenticated]);

  // Create a new scenario
  const createNewScenario = async () => {
    const newScenario = {
      id: `scenario-${Date.now()}`,
      name: `Scenario ${scenarios.length + 1}`,
      audienceName: "",
      budget: 100000,
      channels: [],
      results: null
    };
    
    setScenarios([...scenarios, newScenario]);
    setActiveScenario(newScenario.id);
  };

  // Update an existing scenario
  const updateScenario = (scenarioId, updates) => {
    setScenarios(prevScenarios => 
      prevScenarios.map(scenario => 
        scenario.id === scenarioId ? { ...scenario, ...updates } : scenario
      )
    );
  };

  // Delete a scenario
  const deleteScenario = (scenarioId) => {
    setScenarios(prevScenarios => prevScenarios.filter(scenario => scenario.id !== scenarioId));
    
    // If the active scenario is deleted, set active to the first available
    if (activeScenario === scenarioId && scenarios.length > 1) {
      const remainingScenarios = scenarios.filter(scenario => scenario.id !== scenarioId);
      setActiveScenario(remainingScenarios[0]?.id || null);
    }
  };

  // Calculate reach for a scenario
  const calculateReach = async (scenarioId) => {
    const scenario = scenarios.find(s => s.id === scenarioId);
    if (!scenario) return;

    setLoading(true);
    
    try {
      await refreshToken();
      
      // First, prepare the data through the data preparation API
      const prepResponse = await axios.post(
        'https://n8n.iniai.cloud/webhook/integrated-data',
        {
          audience: scenario.audienceName
        },
        {
          headers: {
            
             'x-api-key': 'auth_k_2f9a8b3c7d6e4f5g9h8i1j2k3l4m5n6',
            'Content-Type': 'application/json'
          }
        }
      );
      
      if (!prepResponse.data || !prepResponse.data.RequestID) {
        throw new Error('Data preparation failed');
      }
      
      // Then calculate reach using the prepared data
      const calcResponse = await axios.post(
        'https://n8n.iniai.cloud/webhook/reach-calculation',
        {
          requestId: prepResponse.data.RequestID,
          scenarioId: scenario.id,
          budget: scenario.budget,
          channels: scenario.channels
        },
        {
          headers: {
             'x-api-key': 'auth_k_2f9a8b3c7d6e4f5g9h8i1j2k3l4m5n6',
            'Content-Type': 'application/json'
          }
        }
      );
      
      // Update the scenario with results
      updateScenario(scenarioId, { 
        results: calcResponse.data,
        dataRequestId: prepResponse.data.RequestID
      });
      
      // Add a message to chat history
      setChatHistory(prev => [
        ...prev, 
        { 
          type: 'system', 
          content: `Calculations complete for "${scenario.name}"` 
        }
      ]);
      
    } catch (error) {
      console.error('Error calculating reach:', error);
      setChatHistory(prev => [
        ...prev, 
        { 
          type: 'system', 
          content: `Error calculating reach: ${error.message}` 
        }
      ]);
    } finally {
      setLoading(false);
    }
  };

  // Compare two or more scenarios
  const compareScenarios = async (scenarioIds) => {
    if (scenarioIds.length < 2) return;
    
    setLoading(true);
    
    try {
      await refreshToken();
      
      const scenariosToCompare = scenarios.filter(s => scenarioIds.includes(s.id));
      
      // Create comparison data from the scenarios
      const comparisonData = {
        scenarioIds,
        scenarios: scenariosToCompare.map(s => ({
          id: s.id,
          name: s.name,
          budget: s.budget,
          audienceName: s.audienceName,
          results: s.results
        }))
      };
      
      setComparison(comparisonData);
      
      // You can also call a specific API if needed for additional comparative analysis
      // const comparisonResponse = await axios.post(...)
      
    } catch (error) {
      console.error('Error comparing scenarios:', error);
      setChatHistory(prev => [
        ...prev, 
        { 
          type: 'system', 
          content: `Error comparing scenarios: ${error.message}` 
        }
      ]);
    } finally {
      setLoading(false);
    }
  };

  // Process natural language query
  const processQuery = async (query) => {
    setChatHistory(prev => [...prev, { type: 'user', content: query }]);
    setLoading(true);
    
    try {
      await refreshToken();
      
      // Send the query to a master agent that can understand and process it
      const response = await axios.post(
        'https://n8n.iniai.cloud/webhook/reach-curve-agent',
        {
          query,
          user: {
            id: user?.id || '',
            email: user?.email || '',
            role: user?.role || '',
            name: user?.name || ''
          },
          scenarios: scenarios,
          activeScenarioId: activeScenario,
          sessionId: `${user?.id || 'anonymous'}-${Date.now()}`,
          context: {
            isAuthenticated,
            timestamp: new Date().toISOString()
          }
        },
        {
          headers: {
            'x-api-key': 'auth_k_2f9a8b3c7d6e4f5g9h8i1j2k3l4m5n6',
            'Content-Type': 'application/json'
          }
        }
      );
      
      // Handle the agent's response
      if (response.data) {
        // Add agent's response to chat
        setChatHistory(prev => [...prev, { type: 'agent', content: response.data.message }]);
        
        // If the agent provides actions to perform, execute them
        if (response.data.actions) {
          handleAgentActions(response.data.actions);
        }
      }
      
    } catch (error) {
      console.error('Error processing query:', error);
      setChatHistory(prev => [
        ...prev, 
        { 
          type: 'agent', 
          content: `I'm sorry, I encountered an error processing your request: ${error.message}` 
        }
      ]);
    } finally {
      setLoading(false);
    }
  };

  // Handle actions returned from the agent
  const handleAgentActions = (actions) => {
    actions.forEach(action => {
      switch (action.type) {
        case 'CREATE_SCENARIO':
          createNewScenario();
          break;
        case 'UPDATE_SCENARIO':
          updateScenario(action.scenarioId, action.updates);
          break;
        case 'CALCULATE_REACH':
          calculateReach(action.scenarioId);
          break;
        case 'COMPARE_SCENARIOS':
          compareScenarios(action.scenarioIds);
          break;
        default:
          console.warn('Unknown action type:', action.type);
      }
    });
  };

  // Get the currently active scenario object
  const currentScenario = scenarios.find(s => s.id === activeScenario);

  return (
    <div className="reach-curve-tool">
      <h1>Reach Curve Tool</h1>
      
      <div className="tool-layout">
        <div className="sidebar">
          <ScenarioPanel 
            scenarios={scenarios}
            activeScenario={activeScenario}
            onSelectScenario={setActiveScenario}
            onCreateScenario={createNewScenario}
            onDeleteScenario={deleteScenario}
            onCompareScenarios={compareScenarios}
          />
          
          <ChatInterface 
            chatHistory={chatHistory}
            onSendQuery={processQuery}
            loading={loading}
          />
        </div>
        
        <div className="main-content">
          {comparison ? (
            <ComparisonView 
              comparison={comparison}
              onClose={() => setComparison(null)}
            />
          ) : currentScenario ? (
            <ReachVisualizer
              scenario={currentScenario}
              onUpdateScenario={(updates) => updateScenario(currentScenario.id, updates)}
              onCalculateReach={() => calculateReach(currentScenario.id)}
              loading={loading}
            />
          ) : (
            <div className="empty-state">
              <p>Create a new scenario to get started</p>
              <button onClick={createNewScenario}>Create Scenario</button>
            </div>
          )}
        </div>
      </div>
    </div>
  );
}

export default ReachCurveTool;